import config from "shared/config";
import { intl } from "shared/storeInfo";

export const DEFAULT_ERROR = { wayToHandle: "ignore", code: "UNKNOWN" };

export const LOW_LEVEL_ERRORS = {
  null: {
    wayToHandle: "display-to-user",
    code: "NO_RESPONSE",
    messages: ["Keine Rückmeldung"],
  },
  500: {
    wayToHandle: "display-to-user",
    code: "SERVER_ERROR",
    messages: [
      "Leider ist ein interner Fehler aufgetreten. " +
        "Bitte kontaktiere das EDEKA StartHub Team - info@starthub.edeka",
    ],
  },
};

export const LOW_LEVEL_FETCH_ERRORS = {
  404: {
    wayToHandle: "display-to-user",
    code: "NOT_FOUND",
    messages: ["Seite nicht vorhanden oder nicht mehr gültig"],
  },
  403: {
    wayToHandle: "display-to-user",
    code: "FORBIDDEN",
    messages: ["Seitenaufruf nicht genehmigt"],
  },
};

export const LOW_LEVEL_SUBMIT_ERRORS = {
  404: {
    wayToHandle: "display-to-user",
    code: "NOT_FOUND",
    messages: ["Datenübermittlung fehlgeschlagen"],
  },
  403: {
    wayToHandle: "display-to-user",
    code: "FORBIDDEN",
    messages: ["Datenübermittlung nicht genehmigt"],
  },
};

export function understandFetchError(reason) {
  const responseErrorType = reason.response ? reason.response.status : null;

  const error =
    LOW_LEVEL_FETCH_ERRORS[responseErrorType] ||
    LOW_LEVEL_ERRORS[responseErrorType] ||
    DEFAULT_ERROR;

  if (config.clientEnv === "development" && error.wayToHandle === "ignore") {
    console.warn("Ignoring FETCH error", reason);
  }

  return error;
}

function matchResourceRecord(requestData, resourceName, resourceIndex) {
  if (!requestData || !requestData.data || !requestData.data.relationships) {
    return null;
  }

  const resources = requestData.data.relationships[`${resourceName}s`];
  if (!resources) {
    return null;
  }

  const resource = resources.data[resourceIndex];
  if (!resource) {
    return null;
  }

  return resource.attributes;
}

function enhanceErrors(errors, meta = {}, resourceName = null) {
  // No error, nothing to do
  if (!errors) {
    return {};
  }

  return errors.map(error => {
    const enhancedError = {
      field: null,
    };
    enhancedError.message = error.detail || error.title;

    if (!error.source || !error.source.pointer) {
      return enhancedError;
    }

    const sourcePointerMatch = error.source.pointer.match(
      /^\/data\/attributes\/([^/]+)(?:\/(\d+)(?:\/([^/]+))?)?$/,
    );
    if (!sourcePointerMatch && config.clientEnv === "development") {
      console.warn(
        "Found a non-understood source pointer string:",
        error.source.pointer,
      );
    }
    if (sourcePointerMatch[2]) {
      enhancedError.field = {
        attribute: sourcePointerMatch[3], // can be undefined
        resourceName: sourcePointerMatch[1],
        resourceIndex: parseInt(sourcePointerMatch[2], 10),
      };
      const resourceRecord = matchResourceRecord(
        meta.cachedRequestData,
        enhancedError.field.resourceName,
        enhancedError.field.resourceIndex,
      );
      if (resourceRecord) {
        enhancedError.field.resourceRecord = resourceRecord;
      }
    } else {
      enhancedError.field = {
        attribute: sourcePointerMatch[1],
      };
    }
    if (enhancedError.field.attribute) {
      let localizedAttribute = '';
      if (resourceName || enhancedError.field.resourceName) {
        localizedAttribute = intl(
          `models.${resourceName || enhancedError.field.resourceName}.${
            enhancedError.field.attribute
          }`,
        );
        localizedAttribute = ` (${localizedAttribute})`
      }
      enhancedError.message = `${enhancedError.message}${localizedAttribute}`;
    }

    return enhancedError;
  });
}

export function understandSubmitError(reason, meta = {}, resourceName = null) {
  const responseErrorType = reason.response ? reason.response.status : null;

  let error =
    LOW_LEVEL_SUBMIT_ERRORS[responseErrorType] ||
    LOW_LEVEL_ERRORS[responseErrorType];

  if (!error) {
    if (
      reason.response &&
      reason.response.data &&
      reason.response.data.errors
    ) {
      const enhancedErrors = enhanceErrors(
        reason.response.data.errors,
        meta,
        resourceName,
      );

      error = {
        wayToHandle: "display-to-user",
        code: "GENERIC",
        messages: enhancedErrors.map(e => e.message),
        fields: enhancedErrors.map(e => e.field),
      };
    } else {
      error = DEFAULT_ERROR;
    }
  }

  if (config.clientEnv === "development" && error.wayToHandle === "ignore") {
    console.warn("Ignoring SUBMIT error", reason);
  }

  return error;
}
